/// <reference lib="dom" />
import { useState, useCallback } from "react";
import type { CasdoorUser } from "@pineseed/gui/types/auth";

// Type for auth config passed to login
type AuthConfig = {
  casdoor: {
    serverUrl: string;
    clientId: string;
    appName: string;
    organizationName: string;
    redirectPath: string;
    signinPath?: string;
    ui: {
      mode: 'redirect' | 'embedded' | 'popup' | 'silent';
      theme?: 'light' | 'dark' | 'auto';
      customization?: Record<string, unknown>;
    };
    sso?: {
      enabled: boolean;
      providers?: string[];
    };
    providerConfig?: Record<string, unknown>;
  };
};

// Type for login result
type LoginResult = {
  user: CasdoorUser;
  tokens: {
    accessToken: string;
    refreshToken?: string;
    tokenType: string;
    expiresIn: number;
    scope: string;
    idToken?: string;
  };
};

export interface GenerateApiKeyResponse {
  apiKey: string;
  expiresIn: number;
}

/**
 * Hook for popup-based API key generation flow
 *
 * Flow:
 * 1. Opens popup for Casdoor authentication
 * 2. Gets JWT from auth callback
 * 3. Exchanges JWT for API key via /auth/keys/generate
 *
 * Minimal implementation reusing existing popup auth pattern from useEdgeAuth
 */
export function useApiKeyPopup(workspaceId: string, apiBase: string, authConfig: AuthConfig) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedKey, setGeneratedKey] = useState<GenerateApiKeyResponse | null>(null);

  const generateApiKeyWithPopup = useCallback(async (ttl: number): Promise<GenerateApiKeyResponse> => {
    setLoading(true);
    setError(null);
    setGeneratedKey(null);

    try {
      console.log('🔄 Starting popup login for API key generation...');

      // Get proper login URL with PKCE from auth handler
      const { AuthHandler } = await import('@pineseed/workspace/handlers/auth-handler');
      const authHandler = AuthHandler.getInstance(authConfig);

      // Initialize the handler
      await authHandler.initialize({
        casdoorConfig: authConfig.casdoor,
      });

      // Get the proper login URL (this creates and stores the PKCE verifier)
      const loginUrl = await authHandler.getLoginUrl();

      const result = await new Promise<LoginResult>((resolve, reject) => {
        // Open popup (same as workspace auth handler)
        const popup = window.open(
          loginUrl,
          'CasdoorAPIKeyGen',
          'width=600,height=700,scrollbars=yes,resizable=yes,status=yes,location=yes,toolbar=no,menubar=no'
        );

        if (!popup) {
          reject(new Error('Popup blocked. Please allow popups for this site.'));
          return;
        }

        // Use postMessage to receive auth callback
        const handleMessage = async (event: MessageEvent) => {
          // Only accept messages from our callback page
          if (!event.origin.includes('localhost') && !event.origin.includes(window.location.hostname)) {
            return;
          }

          if (event.data?.type === 'CASDOOR_AUTH_CALLBACK') {
            window.removeEventListener('message', handleMessage);
            clearTimeout(timeoutId);

            const { code, error: authError, state } = event.data;

            try {
              // Close popup
              popup.close();
            } catch (e) {
              // Ignore COOP errors when closing
            }

            if (authError) {
              reject(new Error(`Auth error: ${authError}`));
              return;
            }

            if (code) {
              console.log('✅ Got auth code via postMessage, exchanging for JWT...');

              try {
                const result = await authHandler.handleCallback(code, state || '');
                console.log('✅ Got JWT from Casdoor');

                await authHandler.clearStoredAuthState();

                resolve(result);
              } catch (error) {
                console.error('❌ Auth callback failed:', error);
                reject(error);
              }
            }
          }
        };

        // Listen for postMessage from callback page
        window.addEventListener('message', handleMessage);

        // Timeout after 5 minutes
        const timeoutId = setTimeout(() => {
          window.removeEventListener('message', handleMessage);
          try {
            if (popup && !popup.closed) popup.close();
          } catch (e) {
            // Ignore COOP errors
          }
          reject(new Error('Login timeout'));
        }, 300000);
      });

      const jwt = result.tokens.accessToken;
      console.log('🔄 Exchanging JWT for API key...');

      // Exchange JWT for API key via /auth/keys/generate
      const response = await fetch(`${apiBase}/auth/keys/generate?workspaceId=${workspaceId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ttl }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data: GenerateApiKeyResponse = await response.json();
      console.log('✅ API key generated:', data.apiKey.substring(0, 8) + '...');

      setGeneratedKey(data);
      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate API key';
      console.error('❌ API key generation failed:', errorMessage);
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiBase, workspaceId, authConfig]);

  const clearKey = useCallback(() => {
    setGeneratedKey(null);
    setError(null);
  }, []);

  return {
    generateApiKeyWithPopup,
    clearKey,
    loading,
    error,
    generatedKey,
  };
}
